import logger from './loggerService';
import ConfigurationService from './ConfigurationService';
import { encryptPayload, decryptPayload, EncryptedBlob } from '../utils/cryptoStorageService';

export interface OfflineRequest {
    id: string;
    url: string;
    method: string;
    body: unknown;
    headers: Record<string, string>;
    timestamp: number;
}

/**
 * OfflineSyncService — Enterprise-Grade Offline Mutation Queue
 *
 * Manages a queue of failed mutations for replay when connectivity is restored.
 * All persisted data is AES-GCM 256-bit encrypted via cryptoStorageService.
 *
 * @security ZERO plaintext writes — encryption failure holds data in volatile memory only.
 * @security Read path is side-effect-free — migration is deferred to next saveQueue call.
 */
class OfflineSyncService {
    private onSyncFailureCallback?: (id: string, url: string, error: { status: number; message?: string }) => void;
    private queue: OfflineRequest[] = [];
    private isProcessing: boolean = false;
    private initialized: boolean = false;

    /** Flag indicating legacy data was detected during load and needs re-encryption on next save */
    private pendingMigration: boolean = false;

    constructor() {
        // Listen for online events
        if (typeof window !== 'undefined') {
            window.addEventListener('online', this.handleOnline);
        }
    }

    public setOnSyncFailure(callback: (id: string, url: string, error: { status: number; message?: string }) => void) {
        this.onSyncFailureCallback = callback;
    }

    private async ensureInitialized(): Promise<void> {
        if (!this.initialized) {
            await this.loadQueue();
            this.initialized = true;
        }
    }

    /**
     * Detects whether a parsed JSON value is an EncryptedBlob structure.
     * Uses structural type checking rather than instanceof for JSON-deserialized data.
     */
    private static isEncryptedBlob(value: unknown): value is EncryptedBlob {
        if (!value || typeof value !== 'object') return false;
        const candidate = value as Record<string, unknown>;
        return (
            typeof candidate.ciphertext === 'string' &&
            typeof candidate.iv === 'string' &&
            typeof candidate.salt === 'string' &&
            typeof candidate.version === 'number'
        );
    }

    /**
     * Safely attempts to parse a raw string as a legacy (unencrypted) OfflineRequest array.
     * Returns the parsed array or null if the data is not valid legacy format.
     */
    private static parseLegacyQueue(raw: string): OfflineRequest[] | null {
        try {
            const parsed: unknown = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed as OfflineRequest[];
            }
            return null;
        } catch {
            return null;
        }
    }

    /**
     * Loads the offline queue from encrypted localStorage.
     *
     * Read path is SIDE-EFFECT-FREE — no writes occur during load.
     * Legacy unencrypted data is flagged for migration on the next save operation.
     */
    private async loadQueue(): Promise<void> {
        try {
            if (typeof window === 'undefined') return;

            const key = ConfigurationService.getOfflineStorageKey();
            const stored = window.localStorage.getItem(key);
            if (!stored) return;

            // Attempt 1: Parse as EncryptedBlob and decrypt
            try {
                const parsed: unknown = JSON.parse(stored);

                if (OfflineSyncService.isEncryptedBlob(parsed)) {
                    const decrypted = await decryptPayload(parsed);
                    if (decrypted && Array.isArray(decrypted)) {
                        this.queue = decrypted as OfflineRequest[];
                    } else {
                        // Decryption returned null — seed unavailable or data corrupt.
                        // The data is permanently inaccessible; clear the stale entry.
                        logger.warn('Encrypted offline queue could not be decrypted — purging stale entry', {
                            context: 'OfflineSyncService',
                        });
                        window.localStorage.removeItem(key);
                        this.queue = [];
                    }
                    return;
                }

                // Attempt 2: Detect legacy unencrypted array format
                const legacyQueue = OfflineSyncService.parseLegacyQueue(stored);
                if (legacyQueue) {
                    logger.info('Legacy unencrypted offline queue detected — flagged for encrypted migration', {
                        context: 'OfflineSyncService',
                        itemCount: legacyQueue.length,
                    });
                    this.queue = legacyQueue;
                    this.pendingMigration = true;
                    return;
                }

                // Neither encrypted blob nor valid array — corrupt data
                logger.warn('Offline queue storage contains unrecognized format — purging', {
                    context: 'OfflineSyncService',
                });
                window.localStorage.removeItem(key);
                this.queue = [];
            } catch (parseError) {
                // JSON.parse or decryption failure — data is corrupt
                logger.error('Failed to parse offline queue from storage — purging corrupt entry', {
                    error: parseError instanceof Error ? parseError.message : String(parseError),
                    context: 'OfflineSyncService',
                });
                window.localStorage.removeItem(key);
                this.queue = [];
            }
        } catch (error) {
            logger.error('Failed to load offline queue', {
                error: error instanceof Error ? error.message : String(error),
            }, 'OfflineSyncService.loadQueue');
            this.queue = [];
        }
    }

    /**
     * Persists the offline queue to localStorage as an AES-GCM encrypted blob.
     *
     * @security NEVER writes plaintext in ANY environment.
     * On encryption failure, data is held in volatile memory only and a
     * high-priority error is logged for operator visibility.
     */
    private async saveQueue(): Promise<void> {
        try {
            if (typeof window === 'undefined') return;
            const key = ConfigurationService.getOfflineStorageKey();

            if (this.queue.length === 0) {
                window.localStorage.removeItem(key);
                this.pendingMigration = false;
                return;
            }

            try {
                const blob = await encryptPayload(this.queue);
                window.localStorage.setItem(key, JSON.stringify(blob));
                // Migration complete — legacy data has been re-encrypted
                if (this.pendingMigration) {
                    logger.info('Legacy offline queue successfully migrated to encrypted format', {
                        context: 'OfflineSyncService',
                        itemCount: this.queue.length,
                    });
                    this.pendingMigration = false;
                }
            } catch (encryptionError) {
                // ZERO PLAINTEXT POLICY: Never fall back to unencrypted writes.
                // The queue remains in volatile memory and will be retried on
                // the next save attempt (e.g., when a new item is added or when
                // the user authenticates and the encryption seed becomes available).
                logger.error('Encryption failed — offline queue held in volatile memory only. Data will NOT be persisted until encryption succeeds.', {
                    error: encryptionError instanceof Error ? encryptionError.message : String(encryptionError),
                    queueSize: this.queue.length,
                    context: 'OfflineSyncService',
                });
            }
        } catch (error) {
            logger.error('Failed to save offline queue', {
                error: error instanceof Error ? error.message : String(error),
            }, 'OfflineSyncService.saveQueue');
        }
    }

    public async addToQueue(url: string, method: string, body: unknown, headers: Headers): Promise<void> {
        await this.ensureInitialized();
        const headerObj: Record<string, string> = {};
        headers.forEach((val: string, key: string) => {
            headerObj[key] = val;
        });

        const request: OfflineRequest = {
            id: crypto.randomUUID(),
            url,
            method,
            body,
            headers: headerObj,
            timestamp: Date.now(),
        };

        this.queue.push(request);
        await this.saveQueue();
        logger.info('Request queued for offline sync', { id: request.id, url, method });
    }

    private handleOnline = async () => {
        logger.info('Connectivity restored. Processing offline queue...', { queueSize: this.queue.length });
        await this.processQueue();
    };

    /**
     * Replays queued requests.
     * Note: This introduces a circular dependency if we import apiClient here.
     * To solve this, we will use fetch directly or inject the client.
     * For simplicity and strictness, we'll use fetch but ensure we respect auth headers stored in the request.
     */
    public async processQueue() {
        await this.ensureInitialized();
        if (this.isProcessing || this.queue.length === 0) return;

        this.isProcessing = true;
        const tempQueue = [...this.queue];
        // Clear queue so we don't double process. If fail, we re-add.
        this.queue = [];
        await this.saveQueue();

        const failedRequests: OfflineRequest[] = [];

        for (const req of tempQueue) {
            try {
                logger.info('Replaying offline request', { id: req.id, url: req.url });

                const response = await fetch(req.url, {
                    method: req.method,
                    headers: req.headers,
                    body: req.body ? (typeof req.body === 'string' ? req.body : JSON.stringify(req.body)) : undefined,
                });

                if (!response.ok) {
                    if (response.status >= 500) {
                        failedRequests.push(req);
                    } else {
                        // Permanent failure (4xx) — notify callback and drop
                        logger.warn('Offline request failed on replay - dropping', { id: req.id, status: response.status });
                        this.onSyncFailureCallback?.(req.id, req.url, { status: response.status });
                    }
                }
            } catch (error) {
                logger.error('Network error during sync - re-queueing', error, 'OfflineSyncService.processQueue');
                failedRequests.push(req);
            }
        }

        if (failedRequests.length > 0) {
            this.queue = [...failedRequests, ...this.queue]; // Put failed back at front (roughly)
            await this.saveQueue();
        }

        this.isProcessing = false;
    }

    public async getQueueSize(): Promise<number> {
        await this.ensureInitialized();
        return this.queue.length;
    }
}

export default new OfflineSyncService();

